/**
 * Build a "get directions" link for the device doing the asking.
 *
 * Sending an iPhone to google.com/maps opens a web page inside Safari with a
 * banner asking to install the app, which is a worse experience than the maps
 * app that device already has. Apple platforms get maps.apple.com, which the
 * OS hands straight to Maps; everything else gets Google Maps, which likewise
 * deep-links into the Android app when it is installed.
 *
 * Resolve this at click time, not at render, so a server-rendered page cannot
 * bake one platform's answer into HTML served to everyone.
 */

export type DirectionsTarget = {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Google place id, the most precise destination Google Maps accepts. */
  googlePlaceId?: string | null;
};

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS reports as a Mac, so the touch-point check catches those too.
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  const mac = /Macintosh|Mac OS X/i.test(ua);
  return iOS || mac;
}

/** Postal address as one line, for the platforms that want text not coordinates. */
export function formatDestinationAddress(target: DirectionsTarget): string {
  return [target.address, target.city, target.postalCode]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ');
}

export function buildDirectionsUrl(target: DirectionsTarget): string {
  const address = formatDestinationAddress(target);
  const hasCoords =
    typeof target.latitude === 'number' &&
    typeof target.longitude === 'number' &&
    Number.isFinite(target.latitude) &&
    Number.isFinite(target.longitude);
  const coords = hasCoords ? `${target.latitude},${target.longitude}` : '';

  if (isApplePlatform()) {
    const params = new URLSearchParams();
    // Coordinates are unambiguous; the address is the readable fallback.
    params.set('daddr', coords || address || (target.name ?? ''));
    if (target.name) params.set('q', target.name);
    params.set('dirflg', 'd');
    return `https://maps.apple.com/?${params.toString()}`;
  }

  const params = new URLSearchParams({ api: '1' });
  params.set('destination', coords || address || (target.name ?? ''));
  // With a place id Google shows the business card rather than a dropped pin.
  if (target.googlePlaceId) params.set('destination_place_id', target.googlePlaceId);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Open directions in a new tab. Safe to call straight from an onClick. */
export function openDirections(target: DirectionsTarget): void {
  if (typeof window === 'undefined') return;
  window.open(buildDirectionsUrl(target), '_blank', 'noopener,noreferrer');
}
